import { ColumnConfig, Waffles } from "@app/shared/waffles";
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ExceptionsFiltersContext } from "@app/exceptions/contexts/ExceptionsFiltersContext";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { PaginationResult, SortDirections, WafflesPaginationProps } from "@app/shared/waffles/exports";
import ExceptionsService from "@app/exceptions/exceptionsService";
import {
  EditExceptionModel,
  ExceptionsDataModel,
  ExceptionsPaginationResult,
  ExceptionsTableData
} from "@app/exceptions/interfaces";
import moment from "moment";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { FlyoutLink } from "@app/shared/Flyouts";
import EditExceptionFlyout from "@app/exceptions/EditExceptionFlyout";
import ExceptionsFilters from "@app/exceptions/ExceptionsFilters";
import {
  SpecialtyGroupExceptionHistoryTimeline
} from "@app/projects/network-assessment/SpecialtyGroupExceptionHistoryTimeline";
import ArchiveExceptionsModal from "@app/exceptions/ArchiveExceptionsModal";
import ExpireModal from "@app/exceptions/ExpireModal";
import { Accordion, AccordionContainer, formatDateTime, PageSection } from "@app/shared";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import {
  SortExceptionColumn,
  SpecialtyGroupExceptionGrade
} from "@app/projects/results/common/specialtyGroupException";
import useGapFillStatusDescriptions from "@app/projects/network-assessment/adequacy/hooks/useGapFillStatusDescriptions";
import MetricsTiles, { MetricsTileData } from "@app/shared/MetricsTiles";
import startCase from "lodash/startCase";

interface ExceptionsDataGridProps {
  exceptionsData: ExceptionsPaginationResult<ExceptionsDataModel> | null;
  isArchivedTable: boolean;
  setExceptionsData: React.Dispatch<React.SetStateAction<PaginationResult<ExceptionsDataModel> | null>>;
}

export const ExceptionsOverview: FC<ExceptionsDataGridProps> = ({
                                                                  exceptionsData,
                                                                  isArchivedTable,
                                                                  setExceptionsData
                                                                }: ExceptionsDataGridProps) => {
  const exceptionFilterContext = useContext(ExceptionsFiltersContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const exceptionsService = useRef(new ExceptionsService()).current;
  const [loading, setLoading] = useState<boolean>(true);
  const [validationError, setValidationError] = useState(false);
  const gapFillStatusDescriptions = useGapFillStatusDescriptions();

  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [clickedExceptionId, setClickedExceptionId] = useState<number>();
  const [isExpireModalOpen, setIsExpireModalOpen] = useState(false);

  const exceptionsColumnConfig: ColumnConfig = {
    actions: {
      displayText: "",
      minWidth: "114%",
      noSort: true,
      sizeContentToFit: true,
      testId: "exception-action"
    },
    attachment: {
      displayText: "Attachment",
      hidden: isArchivedTable || !featureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS),
      minWidth: "75px",
      noSort: true,
      sizeContentToFit: true,
      testId: "exception-attachment",
    },
    comments: {
      displayText: "Comments",
      minWidth: "100px",
      sizeContentToFit: true,
      testId: "exception-duration"
    },
    endDate: {
      displayText: "Expiration Date",
      minWidth: "90px",
      sizeContentToFit: true,
      testId: "exception-end-date",
    },
    gapResolutionStatus: {
      displayText: "Gap Resolution Status",
      hidden: !featureFlags.has(Features.GAP_FILL_STATUS),
      sizeContentToFit: true,
      testId: "gap-resolution-status",
    },
    grade: {
      displayText: "Exception Grade",
      minWidth: "100px",
      sizeContentToFit: true,
      testId: "exception-grade",
      tooltip: "A = Exception has been reviewed, has all necessary documentation attached and is ready for submission.\n" +
        "B = Exception needs some work around the narrative and/or reviewing additional providers before submission.\n" +
        "C = Exception is missing necessary supporting documentation, narrative, and/or excluded providers.",
    },
    hasUnprocessedExcludedProviders: {
      displayText: "Unprocessed Excluded Providers",
      sizeContentToFit: true,
      testId: "exception-unprocessed-excluded-providers",
      tooltip: "This column indicates if an exception currently has unprocessed excluded providers."
    },
    lastUpdated: {
      displayText: isArchivedTable ? "Archived Date" : "Last Updated",
      sizeContentToFit: true,
      testId: "exception-last-updated",
    },
    lineOfBusiness: {
      displayText: "Line of Business",
      sizeContentToFit: true,
      testId: "exception-line-of-business",
    },
    metOverall: {
      displayText: "Met Overall",
      hidden: !featureFlags.has(Features.GAP_FILL_STATUS),
      sizeContentToFit: true,
      testId: "exception-met-overall",
    },
    narrative: {
      displayText: "Narrative",
      minWidth: "100px",
      sizeContentToFit: true,
      testId: "exception-narrative",
    },
    previouslyApproved: {
      displayElement: <span>Previously<br/>Approved</span>,
      minWidth: "100px",
      sizeContentToFit: true,
      testId: "exception-previously-approved",
    },
    processingStatus: {
      displayText: "Processing Status",
      minWidth: "100px",
      sizeContentToFit: true,
      testId: "exception-processing-status",
    },
    projectName: {
      displayText: "Project",
      minWidth: "150px",
      sizeContentToFit: true,
      testId: "exception-project",
    },
    serviceArea: {
      displayText: "Service Area - County SSA",
      minWidth: "150px",
      sizeContentToFit: true,
      testId: "exception-service-area",
    },
    serviceAreaStatuses: {
      displayText: "Service Area Status",
      minWidth: "150px",
      sizeContentToFit: true,
      testId: "exception-service-area-status",
    },
    specialty: {
      displayText: "Specialty",
      sizeContentToFit: true,
      testId: "exception-specialty",
    },
    startDate: {
      displayText: "Start Date",
      minWidth: "90px",
      sizeContentToFit: true,
      testId: "exception-start-date",
    },
    status: {
      displayText: "Exception Status",
      hidden: isArchivedTable,
      minWidth: "100px",
      sizeContentToFit: true,
      testId: "exception-status",
    },
    updatedBy: {
      displayText: isArchivedTable ? "Archived By" : "Updated By",
      minWidth: "100px",
      sizeContentToFit: true,
      testId: "exception-updated-by",
    },
  };

  const handleOpenArchiveModal = (exceptionId: number) => {
    setIsArchiveModalOpen(true);
    setClickedExceptionId(exceptionId);
  }

  const handleOpenExpireModal = (exceptionId: number) => {
    setIsExpireModalOpen(true);
    setClickedExceptionId(exceptionId);
  }

  const paginationProps: WafflesPaginationProps = useMemo<WafflesPaginationProps>(() => {
    const wafflesPaginationProps: WafflesPaginationProps = {
      changePage: (pageNumber: number) => handlePageChange(pageNumber),
      enableBackendPagination: true,
      handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
      pageSizeOptions: [10, 100, 1000],
      recordsPerPage: exceptionFilterContext.pageSize,
      resetToFirstPage: exceptionFilterContext.resetToFirstPage,
      showPageSizeSelector: true,
      totalRecords: (exceptionsData?.totalCount ?? 0)
    }

    return wafflesPaginationProps;
  }, [exceptionFilterContext.pageSize, exceptionFilterContext.resetToFirstPage, exceptionsData?.totalCount]);

  const handlePageSizeChange = (pageSize: number) => {
    exceptionFilterContext.setPageSize(pageSize);
    exceptionFilterContext.setPageIndex(1);
  }

  const handlePageChange = (newPage: number) => {
    if (!exceptionFilterContext.resetToFirstPage) {
      exceptionFilterContext.setPageIndex(newPage);
    }
    if (exceptionFilterContext.resetToFirstPage) {
      exceptionFilterContext.setResetToFirstPage(false);
    }
  };

  const handleCheckboxChange = (selectedKeys: string[]) => {
    // Handle selecting elements
    if (selectedKeys.length > exceptionFilterContext.selectedExceptionIds.length) {
      exceptionFilterContext.setSelectedExceptionIds((prevSelectedIds) => {
        const newSelectedIds = [...prevSelectedIds];
        selectedKeys.forEach((exceptionId) => {
          if (!newSelectedIds.includes(parseInt(exceptionId, 10))) {
            const parsedId = parseInt(exceptionId, 10);
            if (!newSelectedIds.includes(parsedId)) {
              newSelectedIds.push(parsedId);
            }
          }
        });
        return newSelectedIds;
      });
    }

    // Handle unselecting elements
    if (selectedKeys.length < exceptionFilterContext.selectedExceptionIds.length) {
      exceptionFilterContext.setSelectedExceptionIds(prevSelectedIds =>
        prevSelectedIds.filter(exceptionId => selectedKeys.includes(exceptionId.toString()))
      );
    }

    // Set the custom project names and enableRegulatoryExceptionReports based on the selected exceptions
    if (selectedKeys.length > 0) {
      const selectedExceptionIds = selectedKeys.map(key => parseInt(key, 10));
      const selectedProjects = exceptionsTableData.filter(data => selectedExceptionIds.includes(data.exceptionId));

      const projectNames: string[] = [];
      const enableRegulatoryExceptionReports: boolean[] = [];
      const haveRemovedServiceArea: boolean[] = [];
      const haveRemovedSpecialty: boolean[] = [];

      selectedProjects.forEach(project => {
        projectNames.push(project.projectName.props.children);
        enableRegulatoryExceptionReports.push(project.enableRegulatoryExceptionReports);
        haveRemovedServiceArea.push(project.hasRemovedServiceArea);
        haveRemovedSpecialty.push(project.hasRemovedSpecialty);
      });

      exceptionFilterContext.setCustomProjectName(projectNames);
      exceptionFilterContext.setEnableRegulatoryExceptionReports(enableRegulatoryExceptionReports);
      exceptionFilterContext.setHaveRemovedServiceArea(haveRemovedServiceArea);
      exceptionFilterContext.setHaveRemovedSpecialty(haveRemovedSpecialty);
    } else {
      exceptionFilterContext.setCustomProjectName([]);
      exceptionFilterContext.setEnableRegulatoryExceptionReports([]);
      exceptionFilterContext.setHaveRemovedServiceArea([]);
      exceptionFilterContext.setHaveRemovedSpecialty([]);
    }
  }

  const customSort = (data: ExceptionsTableData[], column: keyof ExceptionsTableData, direction: SortDirections) => {

    exceptionFilterContext.setSortDirection(direction);

    switch (column) {
      case "projectName":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.CustomProjectName);
        break;
      case "serviceArea":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.ServiceArea);
        break;
      case "serviceAreaStatuses":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.RegionSetStatus);
        break;
      case "specialty":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.Specialty);
        break;
      case "gapResolutionStatus":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.GapResolutionStatus);
        break;
      case "grade":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.Grade);
        break;
      case "metOverall":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.MetOverall);
        break;
      case "startDate":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.StartDate);
        break;
      case "endDate":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.EndDate);
        break;
      case "status":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.SpecialtyGroupExceptionStatus);
        break;
      case "previouslyApproved":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.PreviouslyApproved);
        break;
      case "comments":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.HasComments);
        break;
      case "narrative":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.HasNarrative);
        break;
      case "processingStatus":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.ProcessingStatus);
        break;
      case "updatedBy":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.UpdatedByUserFullName);
        break;
      case "lineOfBusiness":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.LineOfBusiness);
        break;
      case "hasUnprocessedExcludedProviders":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.HasUnprocessedExcludedProviders);
        break;
      case "lastUpdated":
      default:
        exceptionFilterContext.setSortColumn(SortExceptionColumn.DateUpdated);
    }

    // Data is undefined and not used here due to passThroughSorting being set to true, but we have to return it either way.
    return data;

  }

  useEffect(() => {
    if (exceptionFilterContext.requestModel == null && exceptionFilterContext.isForArchived && !isArchivedTable) {
      exceptionFilterContext.setIsForArchived(false);
    }
    if (exceptionFilterContext.requestModel == null && !exceptionFilterContext.isForArchived && isArchivedTable) {
      exceptionFilterContext.setIsForArchived(true);
    }
    if (exceptionFilterContext.triggerRefreshData && exceptionFilterContext.requestModel != null) {
      refreshData();
    }
  }, [exceptionFilterContext.requestModel, exceptionFilterContext.triggerRefreshData, exceptionFilterContext.isForArchived]);

  const refreshData = async () => {
    setLoading(true);
    setValidationError(false);
    if (exceptionFilterContext.requestModel.projectIds.length === 0) {
      setExceptionsData(null);
      setLoading(false);
      setValidationError(true);
      return;
    }
    const data = await exceptionsService.getExceptionsData(exceptionFilterContext.requestModel);
    setExceptionsData(data);
    setLoading(false);
    exceptionFilterContext.setTriggerRefreshData(false);
  }

  const handleAttachmentsDownload = async (metadataId: number) => {
    const link = (!!metadataId && metadataId > 0) ?
      `${exceptionsService.downloadSupplementaryDocument(metadataId)}` : `#`;
    authenticatedDownload(link);
  }

  const exceptionsTableData: ExceptionsTableData[] = useMemo<ExceptionsTableData[]>(() => {
    let data: ExceptionsTableData[] = [];

    if (exceptionsData) {
      /* eslint-disable sort-keys */
      data = exceptionsData?.items?.map((exception) => {
        const customProjectId = exception.customProjectId;
        const customProjectName = exception.customProjectName;
        const exceptionId = exception.specialtyGroupExceptionId;
        const serviceArea = `${exception.regionSetDescription}, ${exception.regionSetStateAbbreviation}`;
        const serviceAreaStatuses = exception.regionSetStatus == null ? '' : exception.regionSetStatus;
        const specialty = `${exception.specialtyGroupName} (${exception.specialtyGroupCode})`
        const gapResolutionStatus = gapFillStatusDescriptions[exception.gapResolutionStatus];
        const status = exception.specialtyGroupExceptionStatus;
        const startDate = exception.startDate ? moment(exception.startDate).format("MM/DD/YYYY") : '---';
        const endDate = exception.endDate ? moment(exception.endDate).format("MM/DD/YYYY") : '---';
        const previouslyApproved = exception.previouslyApproved;
        const enableRegulatoryExceptionReports = exception.enableRegulatoryExceptionReports;
        const hasRemovedServiceArea = exception.regionSetStatus == null;
        const hasRemovedSpecialty = exception.specialtyGroupName === "";

        const editExceptionModel: EditExceptionModel = {
          customProjectId,
          customProjectName,
          exceptionId,
          serviceArea,
          specialty,
          status,
          startDate,
          endDate,
          previouslyApproved
        };

        return {
          key: exception.specialtyGroupExceptionId.toString(),
          exceptionId: exception.specialtyGroupExceptionId,
          projectName: (
            <a
              href={`${window.customUIBaseUrl}/projects/${exception.customProjectId}/adequacy/specialty/${exception.specialtyGroupCode}/county/${exception.regionSetCode}`}
              target="_blank" rel="noreferrer">
              {exception.customProjectName}
            </a>
          ),
          serviceArea: `${exception.regionSetDescription}, ${exception.regionSetStateName} - ${exception.regionSetCode}`,
          serviceAreaStatuses,
          specialty: specialty,
          lineOfBusiness: exception.lineOfBusiness,
          gapResolutionStatus: gapResolutionStatus,
          metOverall: exception.metCriteria ? 'Yes' : 'No',
          startDate: exception.startDate ? moment(exception.startDate).format("MM/DD/YYYY") : '---',
          endDate: exception.endDate ? moment(exception.endDate).format("MM/DD/YYYY") : '---',
          hasUnprocessedExcludedProviders: exception.hasUnprocessedExcludedProviders ? 'Yes' : 'No',
          status: exception.specialtyGroupExceptionStatus.charAt(0).toUpperCase() + exception.specialtyGroupExceptionStatus.slice(1),
          grade: exception.grade === SpecialtyGroupExceptionGrade.None ? '---' : startCase(exception.grade),
          previouslyApproved: exception.previouslyApproved ? 'Yes' : 'No',
          comments: exception.hasComments ? 'Yes' : 'No',
          narrative: exception.hasNarrative ? 'Yes' : 'No',
          processingStatus: exception.isUnprocessed ? 'Unprocessed' : 'Processed',
          attachment: featureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS) ?
            (
              <ul className="exceptions__attachment-list">
                {exception?.supplementaryDocuments && exception.supplementaryDocuments.map(file =>
                  <li key={file.supplementaryDocumentMetadataId}>
                    <a href="#"
                       onClick={() => handleAttachmentsDownload(file.supplementaryDocumentMetadataId)}>{file.documentName}</a>
                  </li>
                )}
              </ul>
            ) : null,
          lastUpdated: formatDateTime(exception.dateUpdated),
          updatedBy: exception.updatedByUserFullName,
          enableRegulatoryExceptionReports: enableRegulatoryExceptionReports,
          hasRemovedServiceArea: hasRemovedServiceArea,
          hasRemovedSpecialty: hasRemovedSpecialty,
          actions: (
            <NewActionMenu
              buttonClassNames="p-0 text-dark btn-icon btn-sm"
              testId="exception__action-menu"
              containerClassnames="exceptions__more-actions-container"
              hideArrow={true}
              fixMenu={true}
              fixedMenuOffset={{
                x: -210,
                y: 18,
              }}
              buttonText={<i className="fa fa-ellipsis-v"/>}
            >
              {isArchivedTable ?
                (<>
                  {featureFlags.has(Features.VOLUME_EXCEPTIONS_ARCHIVE) &&
                    <FlyoutLink
                      classNames="d-block action-menu-item font-weight-normal col-12"
                      content={{
                        body: <EditExceptionFlyout
                          editException={editExceptionModel}
                          exceptionStatusOptions={exceptionFilterContext.exceptionStatusOptions}
                          refreshData={refreshData}
                          isForArchived={true}
                        />,
                        title: `Exception: ${customProjectName}`,
                      }}
                      size="40"
                      testId={`exception__reactivate-${exceptionId}`}
                    >
                      Reactivate
                    </FlyoutLink>
                  }
                </>)
                : (<>
                  <FlyoutLink
                    classNames="d-block action-menu-item font-weight-normal col-12"
                    content={{
                      body: <EditExceptionFlyout
                        editException={editExceptionModel}
                        exceptionStatusOptions={exceptionFilterContext.exceptionStatusOptions}
                        refreshData={refreshData}
                        isForArchived={false}
                      />,
                      title: `Exception: ${customProjectName}`,
                    }}
                    size="40"
                    testId={`exception__edit-${exceptionId}`}
                  >
                    Edit
                  </FlyoutLink>
                  {featureFlags.has(Features.VOLUME_EXCEPTIONS_EXPIRE) && exception.specialtyGroupExceptionStatus !== "expired" &&
                    <NewActionMenuItem
                      display={"Expire"}
                      onClick={() => handleOpenExpireModal(exceptionId)}
                      testId={`exception__expire-${exceptionId}`}
                      isSelected={false}
                    />
                  }
                  {featureFlags.has(Features.VOLUME_EXCEPTIONS_ARCHIVE) &&
                    <NewActionMenuItem
                      display="Archive"
                      isSelected={false}
                      onClick={() => handleOpenArchiveModal(exceptionId)}
                      testId={`exception__archive-${exceptionId}`}
                    />
                  }
                </>)}
              <FlyoutLink
                classNames="d-block action-menu-item font-weight-normal col-12"
                content={{
                  body: <SpecialtyGroupExceptionHistoryTimeline
                    regionSetSpecialtyGroupId={exception.regionSetSpecialtyGroupId}
                    projectId={exception.customProjectId}/>,
                  title: "View History",
                }}
                testId="specialty-group-exception-editor__exception-history-flyout"
              >
                View History
              </FlyoutLink>
            </NewActionMenu>
          )
        };
      });
      /* eslint-enable sort-keys */
    }
    setLoading(false);
    return data;
  }, [exceptionsData, featureFlags]);

  const startRecord: number = useMemo<number>(() => {
    if (exceptionFilterContext.pageIndex != undefined && exceptionFilterContext.pageSize != undefined) {
      return (exceptionFilterContext.pageIndex - 1) * exceptionFilterContext.pageSize + 1;
    }
  }, [exceptionFilterContext.pageIndex, exceptionFilterContext.pageSize]);

  const endRecord: number = useMemo<number>(() => {
    if (exceptionsData?.totalCount != undefined) {
      return Math.min(exceptionFilterContext.pageIndex * exceptionFilterContext.pageSize, exceptionsData?.totalCount);
    }
  }, [exceptionFilterContext.pageIndex, exceptionFilterContext.pageSize, exceptionsData?.totalCount]);

  const metricsTileData = useMemo<MetricsTileData[] | undefined>(() => {
    if (validationError) {
      return [];
    } else if (!exceptionsData) {
      return undefined;
    }
    const totalAsString = exceptionsData.totalCount.toString().padStart(2, '0');
    return [
      {
        label: "Total Exceptions:",
        testId: "exceptions-overview__total-exceptions",
        value: totalAsString
      },
      {
        label: "Appealed:",
        testId: "exceptions-overview__appealed",
        value: `${exceptionsData.appealed.toString().padStart(totalAsString.length, '0')} / ${totalAsString}`
      },
      {
        label: "Approved:",
        testId: "exceptions-overview__approved",
        value: `${exceptionsData.approved.toString().padStart(totalAsString.length, '0')} / ${totalAsString}`
      },
      {
        label: "Denied:",
        testId: "exceptions-overview__denied",
        value: `${exceptionsData.denied.toString().padStart(totalAsString.length, '0')} / ${totalAsString}`
      },
      {
        label: "Expired:",
        testId: "exceptions-overview__expired",
        value: `${exceptionsData.expired.toString().padStart(totalAsString.length, '0')} / ${totalAsString}`
      },
      {
        label: "Pending:",
        testId: "exceptions-overview__pending",
        value: `${exceptionsData.pending.toString().padStart(totalAsString.length, '0')} / ${totalAsString}`
      },
      {
        label: "Submitted:",
        testId: "exceptions-overview__submitted",
        value: `${exceptionsData.submitted.toString().padStart(totalAsString.length, '0')} / ${totalAsString}`
      }
    ]
  }, [exceptionsData, validationError]);

  return (
    <PageSection>
      <ExceptionsFilters/>
      {(startRecord != undefined && endRecord != undefined) &&
        <p>Displaying {startRecord}-{endRecord} of {exceptionsData?.totalCount} records</p>
      }
      {!isArchivedTable &&
        <AccordionContainer>
          <Accordion
            testId="exceptions-overview__key-metrics-accordion"
            title="Key Metrics"
            defaultOpen={true}
          >
            <MetricsTiles data={metricsTileData} loading={loading}/>
          </Accordion>
        </AccordionContainer>
      }
      <Waffles<ExceptionsTableData>
        columns={exceptionsColumnConfig}
        passThroughSorting={true}
        customSort={customSort}
        data={exceptionsTableData || []}
        enablePagination={true}
        handleCheckboxChange={handleCheckboxChange}
        loading={loading}
        paginationProps={paginationProps}
        selectedKeys={exceptionFilterContext.selectedExceptionIds.map(num => num.toString())}
        showCheckboxes={!isArchivedTable}
        testId="exceptions-waffles-table"
      />
      <ArchiveExceptionsModal
        isModalOpen={isArchiveModalOpen}
        setIsModalOpen={setIsArchiveModalOpen}
        singleExceptionId={clickedExceptionId}
        resetSelectedCheckboxes={() => {
          exceptionFilterContext.setSelectedExceptionIds([])
        }}
      />
      <ExpireModal
        isModalOpen={isExpireModalOpen}
        setIsModalOpen={setIsExpireModalOpen}
        singleExceptionId={clickedExceptionId}
        resetSelectedCheckboxes={() => {
          exceptionFilterContext.setSelectedExceptionIds([])
        }}
      />
    </PageSection>
  )
}